/* ==========================================================================
   Ozeret -- shared presentational pieces
   ========================================================================== */

import type { ReactNode } from 'react';
import type { Cleaner, House } from '../types';
import { jobLabel, lang } from '../lib/domain';
import { t } from '../lib/i18n';
import { photoUrl } from '../api';

/* -------------------------------------------------------------- wordmark */

/** The name, set the way it was signed off: Newsreader, an orange apostrophe
 *  after the O, the orange dot at the end. One component, so the sidebar,
 *  the sign-in screen and anywhere else can never drift apart. */
/* The hanger beside the name: the teal bar and the orange hook of the app
   icon, without its tile. The name itself is unchanged. */
const HANGER_BAR = 'M84 72C65.8 77.2 46 78 35 86C26 92.55 30 104 44 102C70 98.3 118 80 150 70C164 65.6 174 72 171 84C168.75 93 162 99 156 104';
const HANGER_HOOK = 'M79 57C75 44 84 32 97 32C110 32 117 42 114 52C111 62 98 68 84 72';

export function Wordmark({ size }: { size?: number }) {
  return (
    <span className="wordmark" style={size ? { fontSize: size } : undefined} aria-label="Ozeret">
      <svg className="wm-mark" viewBox="12 18 176 106" aria-hidden="true">
        <path d={HANGER_BAR} stroke="var(--wordmark)" />
        <path d={HANGER_HOOK} stroke="var(--bergamot)" />
      </svg>
      O<span className="apos">’</span>zeret<span className="dot" />
    </span>
  );
}

/* ---------------------------------------------------------------- avatar */

/* photoId is optional: a review author and a shared-link preview are people
   too, and neither carries one. */
type Person = Pick<Cleaner | House, 'initials' | 'hue'> & { photoId?: string | null };

export function Avatar({ p, size }: { p: Person; size?: 'sm' | 'lg' }) {
  /* Initials on a colour are the fallback, not a placeholder waiting to be
     replaced: plenty of people will never add a picture, and a grey silhouette
     for all of them would make the app look empty. The colour stays behind the
     photo, so a picture that fails to load leaves the circle it was in rather
     than a hole. */
  const photoId = (p as { photoId?: string | null }).photoId;
  return (
    <div
      className={'avatar' + (size ? ' ' + size : '')}
      style={{ background: `hsl(${p.hue} 42% 44%)` }}
    >
      {photoId
        ? <img src={photoUrl(photoId)} alt="" loading="lazy" />
        : p.initials}
    </div>
  );
}

/* ----------------------------------------------------------------- stars */

function Star({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill={filled ? 'var(--bergamot)' : 'var(--line-strong)'}>
      <path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2Z" />
    </svg>
  );
}

export function Stars({ value, big }: { value: number; big?: boolean }) {
  return (
    <>
      <span className={'stars' + (big ? ' lg' : '')}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} filled={value >= i - 0.25} />
        ))}
      </span>{' '}
      <span className="starnum">{value.toFixed(1)}</span>
    </>
  );
}

export function StarPicker({
  value, onPick,
}: { value: number; onPick: (n: number) => void }) {
  return (
    <div className="starpick">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={value >= n ? 'on' : ''}
          onClick={() => onPick(n)}
          aria-label={n + ' stars'}
        >
          <Star filled={value >= n} />
        </button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- chips */

export function JobChips({ ids, max = 99 }: { ids: string[]; max?: number }) {
  const rest = ids.length - max;
  return (
    <>
      {ids.slice(0, max).map((id) => (
        <span className="chip tile" key={id}>{jobLabel(id)}</span>
      ))}
      {rest > 0 && <span className="chip">+{rest}</span>}
    </>
  );
}

export function LangChips({ ids, max = 4 }: { ids: { id: string }[]; max?: number }) {
  return (
    <>
      {ids.slice(0, max).map((l) => (
        <span className="chip outline" key={l.id}>{lang(l.id)}</span>
      ))}
    </>
  );
}

/* --------------------------------------------------------------- layout */

export function Card({
  children, className = '', onClick, recur,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  recur?: boolean;
}) {
  const cls = ['card', onClick ? 'tap' : '', recur ? 'recur' : '', className]
    .filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }
          : undefined
      }
    >
      {/* A real element rather than an absolutely-positioned ::before. The
          old one floated over the card and landed on the avatar; this one
          takes up its own room, so it cannot collide with anything. */}
      {recur && <span className="recur-tag">{t('everyweek')}</span>}
      {children}
    </div>
  );
}

export function SectionHead({
  title, action, onAction,
}: { title: string; action?: string; onAction?: () => void }) {
  return (
    <div className="sechead">
      <h3>{title}</h3>
      {action && <button type="button" onClick={onAction}>{action}</button>}
    </div>
  );
}

export interface SegOption<T extends string> {
  id: T;
  label: string;
}

export function Seg<T extends string>({
  options, value, onChange,
}: { options: SegOption<T>[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="segrow">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={value === o.id ? 'on' : ''}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A selectable pill. `on` renders the tick. */
export function Pick({
  label, on, onClick,
}: { label: ReactNode; on?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={'pick' + (on ? ' on' : '')} onClick={onClick}>
      {on && <span className="tick">✓</span>}
      {label}
    </button>
  );
}

export function Note({
  children, warm,
}: { children: ReactNode; warm?: boolean }) {
  return <div className={'note' + (warm ? ' warm' : '')}>{children}</div>;
}

export function MatchBar({ score }: { score: number }) {
  return <div className="matchbar"><i style={{ width: score + '%' }} /></div>;
}

export function Divider() { return <div className="divider" />; }

export function Empty({
  big, sub, action, onAction,
}: { big: string; sub?: string; action?: string; onAction?: () => void }) {
  return (
    <div className="empty">
      <div className="big">{big}</div>
      {sub && <p className="sub">{sub}</p>}
      {action && (
        <button type="button" className="btn ghost sm" onClick={onAction}>{action}</button>
      )}
    </div>
  );
}

export function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={'switch' + (on ? ' on' : '')}
      onClick={onClick}
      role="switch"
      aria-checked={on}
    >
      <i />
    </button>
  );
}

export function Stepper({
  value, onStep, format,
}: { value: number; onStep: (delta: number) => void; format?: (v: number) => string }) {
  return (
    <div className="stepper">
      <button type="button" onClick={() => onStep(-1)} aria-label="Less">−</button>
      <span className="val">{format ? format(value) : value}</span>
      <button type="button" onClick={() => onStep(1)} aria-label="More">+</button>
    </div>
  );
}

export function Slider({
  value, min = 15, max = 200, step = 1, onChange, minLabel, maxLabel,
}: {
  value: number; min?: number; max?: number; step?: number;
  onChange: (v: number) => void; minLabel?: string; maxLabel?: string;
}) {
  return (
    <div className="slider">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="ends">
        <span>{minLabel ?? '$' + min}</span>
        <span>{maxLabel ?? '$' + max}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- progress */

const TRACK_STEPS = ['Posted', 'Chosen', 'Underway', 'Done', 'Reviewed'];

export function StatusTrack({ step }: { step: number }) {
  return (
    <>
      <div className="track">
        {TRACK_STEPS.map((s, i) => (
          <span key={s} style={{ display: 'contents' }}>
            <div className={'node ' + (i < step ? 'done' : i === step ? 'now' : '')}>
              {i < step ? '✓' : i + 1}
            </div>
            {i < TRACK_STEPS.length - 1 && <div className={'line ' + (i < step ? 'done' : '')} />}
          </span>
        ))}
      </div>
      <div className="tracklabels">
        {TRACK_STEPS.map((s) => <span key={s}>{t('tx_track_' + s.toLowerCase())}</span>)}
      </div>
    </>
  );
}

/* ------------------------------------------------------- rating breakdown */

export function DimBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="dim">
      <span className="lbl">{label}</span>
      <span className="starnum">{value.toFixed(1)}</span>
      <span className="bar"><i style={{ width: (value / 5) * 100 + '%' }} /></span>
    </div>
  );
}

/** A search box. Used wherever a list got long enough to scroll past. */
export function SearchBox({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="searchbox">
      <input
        className="input"
        type="search"
        value={value}
        placeholder={placeholder}
        autoCorrect="off"
        autoCapitalize="none"
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button type="button" className="searchclear" onClick={() => onChange('')} aria-label="Clear">
          ✕
        </button>
      )}
    </div>
  );
}

/** Loose matching, so "kosher kitchen" finds "Kosher kitchen (meat / dairy)". */
export function matches(haystack: string, needle: string): boolean {
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  const h = haystack.toLowerCase();
  return n.split(/\s+/).every((word) => h.includes(word));
}

/* ------------------------------------------------------------------ form */

export function Field({
  label, children, hint,
}: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}
